import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query"
import ModelNumbersService from "services/modelNumbers.service"
import { MIN_SEARCH_CHARS } from "helpers/modelNumberCatalogue.helper"
import type { TDimensions, TModelNumberPricePayloadItem } from "types/modelNumbers.type"

type TAddModelNumberPayload = {
	formData: FormData
}

export const useGetModelNumbers = () => {
	return useQuery({
		queryKey: ["getModelNumbers"],
		queryFn: () => ModelNumbersService.getModelNumbers(),
		select: (response) => response.data,
	})
}

export const useGetModelNumber = (id: number, enabled = true) => {
	return useQuery({
		queryKey: ["getModelNumber", id],
		queryFn: () => ModelNumbersService.getModelNumber(id),
		select: (response) => response.data,
		enabled: enabled && !!id,
	})
}

/**
 * Catalogue-wide model-number search (REQ-398) — `GET /model-numbers/?name_contains=`.
 *
 * Gated on `MIN_SEARCH_CHARS` so the unfiltered catalogue is never requested, and
 * keyed by the term so react-query caches each search. Pass an ALREADY DEBOUNCED
 * term: every distinct value is a request.
 *
 * Rows come back complete (`thumbnails_url` + `prices` inline), so nothing here
 * needs a per-row follow-up.
 */
export const useSearchModelNumbers = (nameContains: string, enabled = true) => {
	const term = nameContains.trim()

	return useQuery({
		queryKey: ["searchModelNumbers", term],
		queryFn: () => ModelNumbersService.getModelNumbersCatalogue({ nameContains: term }),
		select: ({ data }) => data,
		enabled: enabled && term.length >= MIN_SEARCH_CHARS,
	})
}

/**
 * Fetches a known set of model numbers in ONE request —
 * `GET /model-numbers/?model_number_ids=`. Used by the object-category scope,
 * whose row list is built from `/categories/` (no images, no prices) and which
 * previously paid two requests PER ROW for the same information.
 *
 * The query key sorts the ids so the same set in a different order is a cache hit.
 * `keepPrevious` holds the last result while a changed id set loads (no flicker for REQ-250's warnings).
 */
export const useGetModelNumbersByIds = (ids: number[], enabled = true, keepPrevious = false) => {
	const sortedIds = [...ids].sort((a, b) => a - b)

	return useQuery({
		queryKey: ["modelNumbersByIds", sortedIds.join(",")],
		queryFn: () => ModelNumbersService.getModelNumbersCatalogue({ modelNumberIds: sortedIds }),
		select: ({ data }) => data,
		enabled: enabled && sortedIds.length > 0,
		placeholderData: keepPrevious ? keepPreviousData : undefined,
	})
}

export const useGetModelNumberImageLink = (id: number) => {
	return useQuery({
		queryKey: ["getModelNumberImageLink", id],
		queryFn: () => ModelNumbersService.getModelNumberImageLink(id),
		select: (response) => response.data,
		enabled: !!id,
	})
}

export const useAddModelNumber = () => {
	return useMutation({
		mutationFn: ({ formData }: TAddModelNumberPayload) => ModelNumbersService.addModelNumber(formData),
	})
}

export const useEditModelNumber = () => {
	return useMutation({
		mutationFn: ({ modelNumber, id }: { modelNumber: string; id: number }) =>
			ModelNumbersService.editModelNumber(modelNumber, id),
	})
}

export const useDeleteModelNumber = () => {
	return useMutation({
		mutationFn: (id: number) => ModelNumbersService.deleteModelNumber(id),
	})
}

export const useUploadModelNumberImage = () => {
	return useMutation({
		mutationFn: ({ id, image }: { id: number; image: File }) => ModelNumbersService.uploadModelNumberImage(id, image),
	})
}

export const useUpdateModelNumberCategories = () => {
	return useMutation({
		mutationFn: ({ id, relatedCategoryId }: { id: number; relatedCategoryId: number | null }) =>
			ModelNumbersService.updateModelNumberCategories(id, relatedCategoryId),
	})
}

export const useUpdateModelNumberDimensions = () => {
	return useMutation({
		mutationFn: ({ id, dimensions }: { id: number; dimensions: TDimensions }) =>
			ModelNumbersService.updateModelNumberDimensions(id, dimensions),
	})
}

export const useUpdateModelNumberPrices = () => {
	return useMutation({
		mutationFn: ({ id, prices }: { id: number; prices: TModelNumberPricePayloadItem[] }) =>
			ModelNumbersService.updateModelNumberPrices(id, prices),
	})
}
